import { useGetProviderSubscriptionsQuery } from "@/redux/apis/subscription/subscriptionPlansApi";
import { useOperator } from "./useOperator";
import { useSubscriptionAssignment } from "./useSubscriptionAssignment";

/** Operator roles considered "admin" for gating UI (RideMinder internal + operator admins). */
export const ADMIN_ROLES = ["Fa_Access", "Owner", "Admin"];

/** Backend-accepted statuses (the API rejects "expired"); used to find the most recent assignment. */
const ALL_SUBSCRIPTION_STATUSES = ["active", "pending_cancellation", "trial", "cancelled", "suspended", "overdue"] as const;

/**
 * Derives the operator's account state (unsubscribed / solo / subscribed) and admin status.
 *
 * IMPORTANT: "unsubscribed" means the operator HAD a subscription that is now cancelled — NOT merely
 * the absence of an active subscription. Many existing fleet/enterprise operators predate the
 * subscription-assignment system and have no assignment at all; they must NOT be treated as
 * unsubscribed. So we require a most-recent assignment whose status is "cancelled".
 *
 * - `isSubscribed`: has an active / trial / pending-cancellation assignment (includes Solo).
 * - `isSolo`: that active assignment is the "Solo" plan.
 * - `isUnsubscribed`: no active assignment AND the most recent assignment is cancelled.
 *
 * Single source of truth for the inactive banner, the "Subscription Required" lock pane, the sidebar
 * label, menu gating, the login path, and the limited User Accounts view.
 */
export const useOperatorAccess = () => {
  const operator = useOperator();
  const { subscriptionStatus, isLoading } = useSubscriptionAssignment();

  // Most recent assignment across all statuses — distinguishes a lapsed (cancelled) operator from a
  // legacy operator that was never on the subscription system (no assignment at all).
  const { data: latestSubscriptions, isLoading: isLoadingLatest } = useGetProviderSubscriptionsQuery(
    { providerId: operator?.id ?? "", status: [...ALL_SUBSCRIPTION_STATUSES], limit: 1, orderBy: ["startDate:DESC"] },
    { skip: !operator?.id }
  );
  const latestSubscription = latestSubscriptions?.[0];

  // `roles` can be momentarily undefined while the operator is hydrating.
  const isAdmin = (operator?.roles ?? []).some((role) => ADMIN_ROLES.includes(role));

  const planName = subscriptionStatus.baseSubscription?.subscription.name ?? null;
  const planSlug = subscriptionStatus.baseSubscription?.subscription.slug ?? null;
  const isSubscribed = Boolean(subscriptionStatus.baseSubscription);
  // Prefer the stable slug; fall back to the name match until the backend slug field rolls out.
  const isSolo = planSlug ? planSlug === "solo" : (planName?.toLowerCase().includes("solo") ?? false);

  // Only "unsubscribed" when there is positive evidence of a cancelled subscription — never from the
  // mere absence of one (which would wrongly flag every legacy operator).
  const isUnsubscribed = !isLoading && !isLoadingLatest && !isSubscribed && latestSubscription?.status === "cancelled";

  return { isAdmin, isSolo, isSubscribed, isUnsubscribed, planName, isLoading: isLoading || isLoadingLatest };
};
